import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserProfile, webpages_Membership, webpages_Role, webpages_UsersInRole } from 'app/shared/models/domain/data.model';
import { PageRequest } from 'app/shared/models/request/page-request.model';
import { PageResponse } from '@iapmoit/library.angular.core';
import { environment } from 'environments/environment';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { UserRoleView } from './admin-users.types';
import { ClientContactService } from 'app/shared/services/client-contact.service';
import { toClientContactAssociation } from 'app/shared/services/client-contact.model';

@Injectable({
    providedIn: 'root'
})
export class AdminUserService {
    baseUrl = environment.apiBaseUrl;
    httpOptions: any = {
        withCredentials: true,
        observe: 'response' as const
    };

    _roleDataUpdated: BehaviorSubject<any> = new BehaviorSubject(null);
    roleDataUpdated$ = this._roleDataUpdated.asObservable();

    _userDataUpdated: BehaviorSubject<any> = new BehaviorSubject(null);
    userDataUpdated$ = this._userDataUpdated.asObservable();
    constructor(
        private http: HttpClient
        , private clientContactService: ClientContactService
    ) { }

    generateDefaultParams(query: string = '') {
        return {
            'orLike[Username]': query
            , 'orLike[Email]': query
            , 'orLike[FirstName]': query
            , 'orLike[LastName]': query
        };
    }

    getUserByUsername(username: string): Observable<UserProfile> {

        const params = {
            'find[Username]': username
            // , 'find[Inactive]': 'IS NULL'
        };

        const httpOptionsLocal = { ...this.httpOptions, params: params };

        return this.http.get<any>(`${this.baseUrl}api/data/user-profile`, httpOptionsLocal)
            .pipe(
                map((users: HttpResponse<PageResponse<UserProfile>>) => {
                    return users.body?.items[0];
                })
            );
    }

    getUserByEmail(email: string): Observable<UserProfile> {

        const params = {
            'find[Email]': email
            // , 'find[Inactive]': 'IS NULL'
        };

        const httpOptionsLocal = { ...this.httpOptions, params: params };

        return this.http.get<any>(`${this.baseUrl}api/data/user-profile`, httpOptionsLocal)
            .pipe(
                map((users: HttpResponse<PageResponse<UserProfile>>) => {
                    return users.body?.items[0];
                })
            );
    }

    getUserById(id): Observable<UserProfile> {
        const params = {
            'find[UserId]': id
        };
        const httpOptionsLocal = { ...this.httpOptions, params: params };
        return this.http.get<any>(`${this.baseUrl}api/data/user-profile`, httpOptionsLocal)
            .pipe(
                map((users: HttpResponse<PageResponse<UserProfile>>) => {
                    return users.body?.items[0];
                })
            );
    }

    getUserByIds(id): Observable<UserProfile[]> {
        const params = {};
        if (id && id?.length) {
            params['find[UserId:in]'] = id.join();
            const httpOptionsLocal = { ...this.httpOptions, params: params };
            return this.http.get<any>(`${this.baseUrl}api/data/user-profile`, httpOptionsLocal)
                .pipe(
                    map((users: HttpResponse<PageResponse<UserProfile>>) => {
                        return users.body?.items;
                    })
                );
        }
        return of([]);
    }

    searchUsers(query: string): Observable<UserProfile[]> {
        const httpOptionsLocal = { ...this.httpOptions, params: this.generateDefaultParams(query)};
        return this.http.get<any>(`${this.baseUrl}api/data/user-profile`, httpOptionsLocal)
            .pipe(
                map((users: HttpResponse<PageResponse<UserProfile>>) => {
                    return users.body?.items;
                })
            );
    }

    // Same as searchUsers but excludes inactive (e.g. soft-deleted personnel) accounts.
    // Used by the personnel access tabs when attaching an existing login account.
    searchActiveUsers(query: string): Observable<UserProfile[]> {
        const params = this.generateDefaultParams(query);
        params['find[inactive]'] = false;

        const httpOptionsLocal = { ...this.httpOptions, params: params };
        return this.http.get<any>(`${this.baseUrl}api/data/user-profile`, httpOptionsLocal)
            .pipe(
                map((users: HttpResponse<PageResponse<UserProfile>>) => {
                    return users.body?.items;
                })
            );
    }

    // Shared debounced autocomplete stream for the personnel access tabs.
    // Wires a control's valueChanges to searchActiveUsers; optional transform
    // lets each caller exclude already-assigned users / cache results.
    buildActiveUserSearch(
        valueChanges: Observable<any>,
        transform?: (users: UserProfile[]) => UserProfile[]
    ): Observable<UserProfile[]> {
        return valueChanges.pipe(
            startWith(''),
            debounceTime(400),
            distinctUntilChanged(),
            switchMap((val: string) => {
                if (!val) {
                    return of([]);
                }
                return this.searchActiveUsers(val).pipe(
                    map((users: UserProfile[]) => transform ? transform(users ?? []) : (users ?? []))
                );
            })
        );
    }

    searchClientUsers(query: string): Observable<UserProfile[]> {
        const params = this.generateDefaultParams(query);
        params['find[ClientId]'] = 'IS NULL';

        const httpOptionsLocal = { ...this.httpOptions, params: params };
        return this.http.get<any>(`${this.baseUrl}api/data/search-client-user-profile-view`, httpOptionsLocal)
            .pipe(
                map((users: HttpResponse<PageResponse<UserProfile>>) => {
                    return users.body?.items;
                })
            );
    }

    addUserRole(userId: number, roleId: number): Observable<UserRoleView[]> {
        return this.http.post(`${this.baseUrl}api/data/admin/user-roles/${userId}/${roleId}`, {})
            .pipe(
                switchMap((data) => {
                    return this.getUserRoles(userId);
                })
            )
    }

    deleteUserRole(userId: number, roleId: number): Observable<UserRoleView[]> {
        return this.http.delete(`${this.baseUrl}api/data/admin/user-roles/${userId}/${roleId}`)
            .pipe(
                switchMap((data) => {
                    return this.getUserRoles(userId);
                })
            )
    }

    getUserRoles(userId: number): Observable<UserRoleView[]> {
        const params = {
            itemsPerPage: environment.defaultAllItemsPerPage,
            currentPage: 1
            // 'find[Inactive]': 'IS NULL'
        };

        const httpOptionsLocal = { ...this.httpOptions, params: params };
        return forkJoin([
            this.http.get<any>(`${this.baseUrl}api/data/admin/roles`, httpOptionsLocal)
                .pipe(map((data: any) => { return data.body.items }))
            , this.http.get(`${this.baseUrl}api/data/admin/user-roles?find[UserId]=${userId}`)
                .pipe(map((data: any) => { return data.items as webpages_UsersInRole[] }))
        ]).pipe(
            map(([roles, userRoles]: [any, webpages_UsersInRole[]]) => {
                if (roles && roles.length) {
                    return roles.map((role: webpages_Role) => {
                        let idx: number = -1;
                        if (userRoles && userRoles.length) {
                            idx = userRoles.findIndex(ur => ur.roleId == role.roleId);
                        }
                        return {
                            userId: userId
                            , roleId: role.roleId
                            , roleName: role.roleName
                            , roleTitle: role.title
                            , description: role.description
                            , selected: (idx > -1)
                        } as UserRoleView;
                    })
                }
                return null;
            })
        );
    }

    getRoles(request: PageRequest): Observable<any> {
        const params = {
            itemsPerPage: request.itemsPerPage,
            currentPage: request.currentPage ?? 1
            // 'find[Inactive]': 'IS NULL'
        };

        if (request.queryRole && request.queryRole !== '') {
            params['orLike[roleName]'] = request.queryRole;
            params['orLike[title]'] = request.queryRole;
            params['orLike[description]'] = request.queryRole;
        }

        if (request['name'] && request['name'] !== '') {
            params['orLike[roleName]'] = request['name'];
        }
        if (request['title'] && request['title'] !== '') {
            params['orLike[roleTitle]'] = request['title'];
        }
        if (request['description'] && request['description'] !== '') {
            params['orLike[description]'] = request['description'];
        }

        const httpOptionsLocal = { ...this.httpOptions, params: params };
        return this.http.get<any>(`${this.baseUrl}api/data/admin/roles`, httpOptionsLocal).pipe(
            switchMap((roles: any) => {
                const items = [];
                roles.items = roles?.body?.items?.map((rows) => {
                    return {
                        roleId: rows.roleId
                        , roleName: rows.roleName
                        , roleTitle: rows.title
                        , description: rows.description
                    } as UserRoleView;
                });
                roles.page = roles?.body?.page;
                return of(roles);
            })
        );
    }

    approveUserAccessUser($event): Observable<any> {
        return forkJoin(
            this.http.post(`${this.baseUrl}api/data/clients/approve-login-access`, $event.row)
            , this.clientContactService.updateAssociation(toClientContactAssociation($event.row))
        );
    }
    approveUserAccess($event): Observable<any> {
        const userProfile = {
            username: $event.row?.email
            , email: $event.row?.email
            , firstName: $event.row?.firstName
            , lastName: $event.row?.lastName
            , inactive: false
        } as UserProfile;
        return forkJoin([
            this.http.get(`${this.baseUrl}api/data/admin/roles?find[RoleName]=Client`).pipe(map((data: PageResponse<webpages_Role>) => data.items[0]))
            , this.http.post(`${this.baseUrl}api/data/user-profile`, userProfile).pipe(map((data: PageResponse<UserProfile>) => data.item))
        ]).pipe(
            switchMap(([role, newProfile]: [webpages_Role, UserProfile]) => {
                $event.row.userId = newProfile.userId;
                return forkJoin([
                    this.http.post(`${this.baseUrl}api/data/member`, { userId: newProfile.userId }).pipe(map((data: PageResponse<webpages_Membership>) => { return data.item; }))
                    , this.http.post(`${this.baseUrl}api/data/admin/user-roles/${newProfile.userId}/${role.roleId}`, {})
                    , this.clientContactService.updateAssociation(toClientContactAssociation($event.row))
                    , this.http.post(`${this.baseUrl}api/data/clients/approve-login-access`, $event.row)
                ]).pipe(switchMap((data) => {
                    return this.http.get(`${this.baseUrl}api/data/user-profile/${newProfile.userId}`).pipe(map((data: PageResponse<UserProfile>) => data.item));
                }));
            }));
    }
}
